import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const CSVParserConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['operation', 'settings']));
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const operations = [
    { value: 'parse', label: 'CSV → JSON', icon: '📊', desc: 'CSV szöveg JSON-né alakítása' },
    { value: 'stringify', label: 'JSON → CSV', icon: '📄', desc: 'JSON adatok CSV-vé alakítása' },
  ];

  const delimiters = [
    { value: ',', label: 'Vessző (,)', icon: ',' },
    { value: ';', label: 'Pontosvessző (;)', icon: ';' },
    { value: '\t', label: 'Tab', icon: '⇥' },
    { value: '|', label: 'Pipe (|)', icon: '|' },
  ];

  return (
    <div className="space-y-3">
      {/* Művelet típus */}
      <CollapsibleSection
        id="operation"
        title="Művelet Típusa"
        icon="🔄"
        color="teal"
        isExpanded={expandedSections.has('operation')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-2">
            {operations.map((op) => (
              <button
                key={op.value}
                onClick={() => updateConfig('operation', op.value)}
                className={`flex items-center p-3 rounded-md border text-sm transition-all ${
                  (node.data.config.operation || 'parse') === op.value
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                }`}
              >
                <span className="text-xl mr-3">{op.icon}</span>
                <div className="flex-1 text-left">
                  <div className="font-medium">{op.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{op.desc}</div>
                </div>
                {(node.data.config.operation || 'parse') === op.value && (
                  <span className="text-teal-500">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Beállítások */}
      <CollapsibleSection
        id="settings"
        title="CSV Beállítások"
        icon="⚙️"
        color="blue"
        isExpanded={expandedSections.has('settings')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              Elválasztó karakter
            </label>
            <div className="grid grid-cols-2 gap-2">
              {delimiters.map((delim) => (
                <button
                  key={delim.value}
                  onClick={() => updateConfig('delimiter', delim.value)}
                  className={`flex items-center justify-center p-2 rounded-md border text-sm transition-all ${
                    (node.data.config.delimiter || ',') === delim.value
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                  }`}
                >
                  <span className="text-lg mr-2">{delim.icon}</span>
                  <span className="text-xs">{delim.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="hasHeader"
              checked={node.data.config.hasHeader !== false}
              onChange={(e) => updateConfig('hasHeader', e.target.checked)}
              className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
            />
            <label htmlFor="hasHeader" className="text-sm text-gray-700 dark:text-gray-300">
              Első sor fejléc
            </label>
          </div>

          {node.data.config.operation === 'parse' && (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="trimValues"
                checked={node.data.config.trimValues !== false}
                onChange={(e) => updateConfig('trimValues', e.target.checked)}
                className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
              />
              <label htmlFor="trimValues" className="text-sm text-gray-700 dark:text-gray-300">
                Szóközök eltávolítása
              </label>
            </div>
          )}

          <div>
            <label className={commonStyles.label}>
              {node.data.config.operation === 'parse' ? 'CSV szöveg' : 'JSON adat'}
            </label>
            <textarea
              value={node.data.config.input || ''}
              onChange={(e) => updateConfig('input', e.target.value)}
              className={commonStyles.textarea}
              rows={5}
              placeholder={node.data.config.operation === 'parse' 
                ? 'name,age,city\nJohn,30,NYC\nJane,25,LA'
                : '{{data}} vagy JSON array'
              }
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* Kimenet Változók */}
      <CollapsibleSection
        id="variables"
        title="Kimenet Változók"
        icon="🔗"
        color="purple"
        isExpanded={expandedSections.has('variables')}
        onToggle={toggleSection}
      >
        <div className="space-y-2">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            A feldolgozás után ezek a változók lesznek elérhetők:
          </div>
          
          <div className="grid grid-cols-1 gap-2">
            {[
              { var: '{{data}}', desc: 'Feldolgozott adatok', example: '[{...}, {...}]' },
              { var: '{{rows}}', desc: 'Sorok száma', example: '150' },
              { var: '{{columns}}', desc: 'Oszlopok száma', example: '5' }
            ].map((item) => (
              <div key={item.var} className="bg-gray-50 dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                    {item.var}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                    {item.example}
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Példák */}
      <CollapsibleSection
        id="examples"
        title="Használati Példák"
        icon="💡"
        color="green"
        isExpanded={expandedSections.has('examples')}
        onToggle={toggleSection}
      >
        <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📊 CSV Import:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Fájl olvasás → CSV Parser → Database Insert
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📄 CSV Export:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Database Query → CSV Parser (stringify) → Fájl írás
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>🔄 Formátum konverzió:</strong>
            <div className="mt-1 font-mono text-[10px]">
              HTTP Request (CSV) → CSV Parser → JSON Processor
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default CSVParserConfig;
